import { GridList, GridListItem, Selection } from "react-aria-components"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import {
  getLanguageName,
  iconForLanguage,
  SUPPORTED_LANGUAGES,
} from "@/utilities/localization"

import styles from "./LanguageSwitcherModal.module.css"
import Modal from "./Modal"

interface LanguageSwitcherModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

export function LanguageSwitcherModal({
  isOpen,
  onOpenChange,
}: LanguageSwitcherModalProps) {
  const { t, i18n } = useTranslation()

  const handleLanguageChange = (keys: Selection) => {
    const selectedLanguage = [...(keys as Set<React.Key>)][0].toString()
    i18n.changeLanguage(selectedLanguage)
  }

  const handleDone = () => {
    onOpenChange(false)
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} modalStyle="centered">
      <Modal.Header>
        <Heading level={2} slot="title">
          {t("settings.changeLanguage.header")}
        </Heading>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <Text>{t("settings.changeLanguage.description")}</Text>
        <GridList
          aria-label={t("settings.changeLanguage.label")}
          selectionMode="single"
          selectedKeys={i18n.resolvedLanguage ? [i18n.resolvedLanguage] : []}
          disallowEmptySelection
          onSelectionChange={handleLanguageChange}
          className={styles.languageList}
        >
          {SUPPORTED_LANGUAGES.map((language) => (
            <GridListItem
              key={language}
              id={language}
              className={styles.languageItem}
              textValue={getLanguageName(language)}
            >
              {({ isSelected }) => (
                <>
                  <Icon
                    name={iconForLanguage(language)}
                    retainOriginalColors={
                      iconForLanguage(language) !== "language"
                    }
                  />
                  <Text>{getLanguageName(language)}</Text>
                  {isSelected && <Icon name="check" />}
                </>
              )}
            </GridListItem>
          ))}
        </GridList>
      </Modal.Body>

      <Modal.Footer>
        {() => (
          <Button buttonType="primary" onPress={handleDone}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default LanguageSwitcherModal
